import { useMemo } from 'react';

import type { Booking } from '@/api/bookings';
import { apiFetch } from '@/api/client';
import { DocketLanguageValues, type DocketLanguage } from '@/api/generated/enums';
import { useApiMutation } from '@/api/mutations';
import { employeesKey, vendorsKey } from '@/api/masters';
import { bookingsKey } from '@/api/bookings';
import { docketLines, docketLinesFor } from '@/pages/print/docket';
import { languageNames } from '@/pages/print/narrativeText';

import { docketLanguagePath, tailorKey, tailorRefOf } from './tailorRef';

/**
 * The language this docket prints in — asked once per tailor, then never again.
 *
 * <p>
 * <strong>Choosing writes the answer onto the tailor.</strong> That is the whole design: a workshop
 * reads one language and it does not change between jobs, so a picker that only affected the sheet
 * in front of you would ask the same question every time and be answered wrongly on the day
 * somebody was in a hurry. Pick Gujarati for Ramesh once and every future docket for Ramesh is
 * Gujarati.
 * </p>
 *
 * <p>
 * <strong>It says so out loud rather than saving quietly.</strong> A control that changes a master
 * record as a side effect of printing is a surprise the first time somebody notices, so the line
 * beneath names the tailor it is being remembered against.
 * </p>
 *
 * <p>
 * Hidden entirely when the sheet has no tailor on either master — a typed name has nowhere to
 * remember an answer, and offering the choice would imply otherwise. Which master it writes to is
 * derived per tailor, so our own karigar and the workshop down the lane are remembered the same way.
 * </p>
 */
export function DocketLanguageChooser({
  booking,
  garmentId,
  onOverride,
  override,
}: {
  booking: Booking;
  garmentId?: number | undefined;
  /** For this print only, when the tailor is not on the master. */
  onOverride: (language: DocketLanguage | undefined) => void;
  override: DocketLanguage | undefined;
}) {
  const lines = useMemo(
    () => (garmentId === undefined ? docketLines(booking) : docketLinesFor(booking, garmentId)),
    [booking, garmentId],
  );

  /*
    Only offered when every sheet in this print goes to the same tailor.

    A booking whose sherwani is with a Gujarati-speaking master and whose trousers are at a
    Hindi-speaking workshop has no single right answer, and each sheet already follows its own
    tailor — so one control over the pair could only be wrong about one of them.

    Compared by `tailorKey` rather than by id, because a tailor is now one of two masters and both
    draw keys from the same IDENTITY seed. On bare ids, vendor 1000004 and employee 1000004 are the
    same number: a print whose sherwani is at an outside workshop and whose trousers are with our
    own karigar would collapse to one tailor and offer to remember an answer against the wrong one.
  */
  const refs = lines.map((line) => tailorRefOf(line.garment));
  const distinct = [...new Set(refs.map(tailorKey))];
  const only = distinct.length === 1 ? (refs[0] ?? null) : null;

  const tailorName = lines[0]?.garment.alterationTailorName ?? null;

  const current: DocketLanguage =
    override ?? lines[0]?.garment.alterationTailorLanguage ?? 'English';

  const path = docketLanguagePath(only);

  const remember = useApiMutation<unknown, DocketLanguage>({
    // Derived from which master the tailor is actually on, never assumed: posting an employee's key
    // to `/api/vendor` would answer 404 for a tailor who plainly exists, and the clerk would be told
    // their own karigar could not be found.
    mutationFn: (language) =>
      apiFetch<unknown>(path ?? '', {
        method: 'PUT',
        body: { docketLanguage: language },
      }),
    success: (_, language) =>
      `${tailorName}'s dockets will print in ${languageNames[language]} from now on.`,
    // The booking carries the resolved language on every garment, so a stale one would keep
    // laying the next sheet out in the language that was just changed. Both masters, because either
    // may be the one that was just written to.
    invalidates: [vendorsKey, employeesKey, bookingsKey],
  });

  if (lines.length === 0) {
    return null;
  }

  const remembered = path !== null;

  return (
    <>
      {/*
        The same segmented group the paper picker is, deliberately.

        The first version was a labelled <select> beside it, which put two different kinds of
        control side by side in one toolbar and read as loose text rather than as a thing to press.
        Three options is the same shape as the paper's two or three, so it is the same control.
      */}
      <div
        role="group"
        aria-label="Docket language"
        className="border-border-color flex overflow-hidden rounded-md border bg-white print:hidden"
      >
        {DocketLanguageValues.map((language) => (
          <button
            key={language}
            type="button"
            aria-pressed={language === current}
            title={`Print this docket in ${languageNames[language]}`}
            className={`cursor-pointer px-3 py-1.5 text-[13px] whitespace-nowrap ${
              language === current
                ? 'bg-dark font-medium text-white'
                : 'hover:bg-light text-gray-900'
            }`}
            onClick={() => {
              onOverride(language);

              // Remembered against the tailor where there is one to remember it against.
              if (remembered) {
                remember.mutate(language);
              }
            }}
          >
            {languageNames[language]}
          </button>
        ))}
      </div>

      {/*
        As terse as the paper's own hint beside it — "Thermal counter printer" is three words, and a
        full sentence at the same weight turns the bar into a strip of grey prose and pushes Print
        off to the right. The sentence lives in the tooltip, where somebody who wants it can get it.
      */}
      <span
        className="text-default text-[12.5px] print:hidden"
        title={
          remembered
            ? `Choosing a language here saves it against ${tailorName}, so future dockets for them print in it without anybody choosing again.`
            : distinct.length > 1
              ? 'These sheets go to different tailors and each prints in its own language. Choosing here changes this print only.'
              : 'This tailor was typed rather than picked, so there is no record to save a language against. Pick them from the tailor list on the garment — your own staff are in it too — to have it remembered.'
        }
      >
        {remembered
          ? `Saved for ${tailorName}`
          : distinct.length > 1
            ? 'Per tailor'
            : 'This print only'}
      </span>
    </>
  );
}
